import React from 'react';
import { defineMessages, t } from '@sm/intl';
import PropTypes from 'prop-types';

import SimpleLogoHeader from '~app/components/SimpleLogoHeader';
import SimpleHero from '~app/components/SimpleHero';
import HeroFormCallout from '~app/components/HeroFormCallout';
import FortuneCallout from '~app/components/FortuneCallout';
import ProductsCalloutList from '~app/components/ProductsCalloutList';

const COPY = defineMessages({
  HERO_FORM_CALLOUT_BOLD_HEADER: {
    id: 'RebrandEpFormCore.HeroFormCalloutBoldHeader',
    defaultMessage: 'Want to create your own surveys?',
    description:
      "[Type: Header][Vis: high] - The Hero's Primary tagline. A question to put into context the subheader beneath this tagline, and to entice users to fillout and submit the form directly below the header.",
  },
  HERO_FORM_CALLOUT_SUBHEADER: {
    id: 'RebrandEpFormCore.HeroFormCalloutSubHeader',
    defaultMessage:
      'Gather opinions from customers, employees, prospects, and more. Use your insights to make better, data-driven decisions.',
    description:
      "[Type: Header][Vis: high] - The Hero's subheader. A value proposition, giving a clear example of what they can get if they fill out the form below.",
  },
  HERO_FORM_CTA_EMAIL_ADDRESS: {
    id: 'RebrandEpFormCore.HeroFormCTAEmailAddress',
    defaultMessage: 'Email Address',
    description: '[Type: Label][Vis: high] - The Label for an email input field.',
  },
  HERO_FORM_CTA_EMAIL_ADDRESS_PLACEHOLDER: {
    id: 'RebrandEpFormCore.HeroFormCTAEmailAddressPlaceholder',
    defaultMessage: 'you@example.com',
    description: '[Type: Label][Vis: high] - The placeholder for an email input field.',
  },
  HERO_FORM_CTA_PASSWORD: {
    id: 'RebrandEpFormCore.HeroFormCTAPassword',
    defaultMessage: 'Password',
    description: '[Type: Label][Vis: high] - The Label for a password input field.',
  },
  HERO_FORM_CTA_PASSWORD_PLACEHOLDER: {
    id: 'RebrandEpFormCore.HeroFormCTAPasswordPlaceholder',
    defaultMessage: '(at least 8 characters)',
    description: '[Type: Label][Vis: high] - The placeholder for a password input field.',
  },
  HERO_FORM_CTA_PRIVACY_CONSENT: {
    id: 'RebrandEpFormCore.HeroFormCTAPrivacyConsent',
    defaultMessage:
      'I agree to the <a href="/mp/legal/terms-of-use/">Terms of Use</a> and understand my data will be handled in accordance with the <a href="/mp/legal/privacy-policy/">Privacy Policy</a>.',
    description:
      '[Type: Label][Vis: high] - The Label for a checkbox consenting to the Terms of Use and Privacy Policy. Only for users located in Germany.',
  },
  HERO_FORM_CTA_NEWSLETTER_OPTOUT: {
    id: 'RebrandEpFormCore.HeroFormCTANewsletterOptOut',
    defaultMessage:
      'I do not want to receive information and offers relevant to SurveyMonkey services via email. You can also opt-out of these emails in your My Account page anytime.',
    description:
      '[Type: Label][Vis: high] - The Label for a checkbox to opt out of Newsletter subscription. Only for users located in Germany.',
  },
  HERO_FORM_CTA_SUBMIT: {
    id: 'RebrandEpFormCore.HeroFormCTASubmit',
    defaultMessage: 'Create Free Account',
    description:
      '[Type: Button][Vis: high] - The button users are required to click to create an account using the values they entered in the email and password feilds.',
  },
  HERO_FORM_CTA_DISCLAIMER: {
    id: 'RebrandEpFormCore.HeroFormCTADisclaimer',
    defaultMessage:
      'By clicking “CREATE FREE ACCOUNT” or Facebook or Google, you agree to the <a href="/mp/legal/terms-of-use/">Terms of Use</a> and <a href="/mp/legal/privacy-policy/">Privacy Policy</a>. You also agree to receive information and offers relevant to our services via email. You can opt-out of these emails in your My Account page anytime.',
    description:
      '[Type: Button][Vis: high] - A legal disclaimer containing links to legal documents Terms of Service and Privacy Policy',
  },

  FORTUNE_CALLOUT_COPY: {
    id: 'RebrandEpFormCore.FortuneCalloutCopy',
    defaultMessage: '95% of the Fortune 500 rely on SurveyMonkey for People Powered Data',
    description: '[Type: Header][Vis: low] - A promotional statistic designed to show corporate trust and prestige',
  },
  FORTUNE_CALLOUT_CTA: {
    id: 'RebrandEpFormCore.FortuneCalloutCTA',
    defaultMessage: 'SIGN UP FREE',
    description: "[Type: Button][Vis: high] - Fortune's CTA which sends people to a sign up page",
  },
});

const RebrandEpFormCore = ({ utSource, calloutItem1, calloutItem2, hideCTAs }) => {
  const HERO_FORM_CTA = {
    ctaFormEmail: t(COPY.HERO_FORM_CTA_EMAIL_ADDRESS),
    ctaFormEmailPlaceholder: t(COPY.HERO_FORM_CTA_EMAIL_ADDRESS_PLACEHOLDER),
    ctaFormNewsletterOptOut: t(COPY.HERO_FORM_CTA_NEWSLETTER_OPTOUT),
    ctaFormPassword: t(COPY.HERO_FORM_CTA_PASSWORD),
    ctaFormPasswordPlaceholder: t(COPY.HERO_FORM_CTA_PASSWORD_PLACEHOLDER),
    ctaFormPrivacyConsent: t(COPY.HERO_FORM_CTA_PRIVACY_CONSENT, {}, { html: true }),
    ctaFormSubmit: t(COPY.HERO_FORM_CTA_SUBMIT),
    disclaimer: t(COPY.HERO_FORM_CTA_DISCLAIMER),
    utSource,
    utSource2: 'rebrand_ep_form',
  };

  return (
    <>
      <Choose>
        <When condition={hideCTAs}>
          <SimpleHero />
          <ProductsCalloutList calloutItem1={calloutItem1} calloutItem2={calloutItem2} utSource={utSource} hideCTAs />
          <FortuneCallout
            copy={t(COPY.FORTUNE_CALLOUT_COPY)}
            ctaText={t(COPY.FORTUNE_CALLOUT_CTA)}
            utSource={utSource}
            utSource2="rebrand_ep_form"
            hideCTAs
          />
        </When>
        <Otherwise>
          <SimpleLogoHeader />
          <HeroFormCallout
            boldHeader={t(COPY.HERO_FORM_CALLOUT_BOLD_HEADER)}
            subHeader={t(COPY.HERO_FORM_CALLOUT_SUBHEADER)}
            formCopy={HERO_FORM_CTA}
          />
          <ProductsCalloutList calloutItem1={calloutItem1} calloutItem2={calloutItem2} utSource={utSource} />
          <FortuneCallout
            copy={t(COPY.FORTUNE_CALLOUT_COPY)}
            ctaText={t(COPY.FORTUNE_CALLOUT_CTA)}
            utSource={utSource}
            utSource2="rebrand_ep_form"
          />
        </Otherwise>
      </Choose>
    </>
  );
};

RebrandEpFormCore.propTypes = {
  utSource: PropTypes.string.isRequired,
  calloutItem1: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    copy: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
    ctaColor: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  calloutItem2: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    copy: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
    ctaColor: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  hideCTAs: PropTypes.bool,
};

RebrandEpFormCore.defaultProps = {
  hideCTAs: false,
};

export default RebrandEpFormCore;
